import React, { Suspense, useEffect } from 'react';

import { useOnboardingRoute } from './hooks/useOnboardingRoute';
import { useOnboardingPersistence } from './hooks/useOnboardingPersistence';
import { OnboardingSheet } from './OnboardingSheet';

/**
 * Container for the modern onboarding experience.
 *
 * Drop-in replacement API for `onboarding-legacy`:
 * - `show` / `setShowOnboarding` drive the sheet open state
 * - `onClose` is invoked after the persistence mutation resolves
 * - `profiles` gates rendering to `Aluno` only (legacy parity)
 * - `title` is rendered in the sheet header
 *
 * Behavioral parity notes (Phase 1):
 * - Returns `null` when the user is not a student.
 * - Returns `null` when the current route has no registered tutorial.
 * - Persists `showTutorialX` flags via the authenticated user mutation,
 *   mirroring the legacy `client.mutate('/users/:id', ...)` call.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {(next: boolean) => void} props.setShowOnboarding
 * @param {() => void} [props.onClose]
 * @param {ReadonlyArray<string>} props.profiles
 * @param {string} [props.title]
 */
export default function Onboarding({ show, setShowOnboarding, onClose, profiles, title }) {
  const isStudent = Array.isArray(profiles) && profiles.includes('Aluno');
  const { config, hasConfig } = useOnboardingRoute();
  const { markAsSeen } = useOnboardingPersistence(config?.flagKey);

  // Placeholder effect kept for Phase 2 parity with legacy `index.js` (lines 41-47),
  // where `display` and `setShowOnboarding` were synchronized with the persisted flag.
  useEffect(() => {
    if (!hasConfig || !isStudent) {
      return;
    }
    // Intentionally empty in Phase 1.
  }, [hasConfig, isStudent]);

  if (!isStudent || !hasConfig || !config) {
    return null;
  }

  const handleOpenChange = async nextOpen => {
    setShowOnboarding(nextOpen);
    if (!nextOpen) {
      await markAsSeen();
      if (typeof onClose === 'function') {
        onClose();
      }
    }
  };

  const TutorialComponent = config.Component;

  return (
    <OnboardingSheet
      open={Boolean(show)}
      onOpenChange={handleOpenChange}
      title={title}
    >
      <Suspense fallback={<div className="p-6 text-sm text-neutral">Carregando...</div>}>
        <TutorialComponent />
      </Suspense>
    </OnboardingSheet>
  );
}
